import { Input, Button, Space, Card, Switch, Typography, Collapse } from 'antd'
import { Plus, Trash2, GitBranch, BookOpen, CheckSquare, Tag as TagIcon } from 'lucide-react'
import { t } from '@/config/translations'
import type {
  GitLabResource,
  GitHubResource,
  ConfluenceResource,
  JiraResource,
} from '@/types/api.types'

const { Text } = Typography
const { Panel } = Collapse

interface ResourcesTabProps {
  gitlabRepos: GitLabResource[]
  githubRepos: GitHubResource[]
  confluenceSpaces: ConfluenceResource[]
  jiraProjects: JiraResource[]
  onGitlabChange: (repos: GitLabResource[]) => void
  onGithubChange: (repos: GitHubResource[]) => void
  onConfluenceChange: (spaces: ConfluenceResource[]) => void
  onJiraChange: (projects: JiraResource[]) => void
}

export function ResourcesTab({
  gitlabRepos,
  githubRepos,
  confluenceSpaces,
  jiraProjects,
  onGitlabChange,
  onGithubChange,
  onConfluenceChange,
  onJiraChange,
}: ResourcesTabProps) {
  // GitLab
  const addGitlabRepo = () => {
    onGitlabChange([
      ...gitlabRepos,
      { url: 'https://gitlab.com', token: '', project_path: '', branch: 'main' },
    ])
  }

  const removeGitlabRepo = (index: number) => {
    onGitlabChange(gitlabRepos.filter((_, i) => i !== index))
  }

  const updateGitlabRepo = (index: number, field: keyof GitLabResource, value: string) => {
    const updated = [...gitlabRepos]
    const current = updated[index]
    if (current) {
      updated[index] = { ...current, [field]: value }
    }
    onGitlabChange(updated)
  }

  // GitHub
  const addGithubRepo = () => {
    onGithubChange([
      ...githubRepos,
      { url: 'https://github.com', token: '', project_path: '', branch: 'main' },
    ])
  }

  const removeGithubRepo = (index: number) => {
    onGithubChange(githubRepos.filter((_, i) => i !== index))
  }

  const updateGithubRepo = (index: number, field: keyof GitHubResource, value: string) => {
    const updated = [...githubRepos]
    const current = updated[index]
    if (current) {
      updated[index] = { ...current, [field]: value }
    }
    onGithubChange(updated)
  }

  // Confluence
  const addConfluenceSpace = () => {
    onConfluenceChange([
      ...confluenceSpaces,
      { url: '', user: '', api_token: '', space_key: '', cloud: false },
    ])
  }

  const removeConfluenceSpace = (index: number) => {
    onConfluenceChange(confluenceSpaces.filter((_, i) => i !== index))
  }

  const updateConfluenceSpace = (
    index: number,
    field: keyof ConfluenceResource,
    value: string | boolean
  ) => {
    const updated = [...confluenceSpaces]
    const current = updated[index]
    if (current) {
      updated[index] = { ...current, [field]: value }
    }
    onConfluenceChange(updated)
  }

  // Jira
  const addJiraProject = () => {
    onJiraChange([
      ...jiraProjects,
      { url: '', user: '', api_token: '', project_key: '', cloud: false },
    ])
  }

  const removeJiraProject = (index: number) => {
    onJiraChange(jiraProjects.filter((_, i) => i !== index))
  }

  const updateJiraProject = (index: number, field: keyof JiraResource, value: string | boolean) => {
    const updated = [...jiraProjects]
    const current = updated[index]
    if (current) {
      updated[index] = { ...current, [field]: value }
    }
    onJiraChange(updated)
  }

  return (
    <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '16px 0' }}>
      <Collapse defaultActiveKey={[]} ghost>
        {/* GitLab Repositories */}
        <Panel
          header={
            <Space>
              <GitBranch size={16} />
              <Text strong>{t('gitlab_repos')}</Text>
              <Text type="secondary">({gitlabRepos.length})</Text>
            </Space>
          }
          key="gitlab"
        >
          <Space vertical style={{ width: '100%' }} size={12}>
            {gitlabRepos.map((repo, index: number) => (
              <Card key={`gitlab-${String(index)}`} size="small">
                <Space vertical style={{ width: '100%' }} size={8}>
                  <Input
                    placeholder={t('gitlab_url')}
                    value={repo.url}
                    onChange={(e) => {
                      updateGitlabRepo(index, 'url', e.target.value)
                    }}
                  />
                  <Input
                    placeholder={t('project_path_placeholder')}
                    value={repo.project_path}
                    onChange={(e) => {
                      updateGitlabRepo(index, 'project_path', e.target.value)
                    }}
                  />
                  <Input.Password
                    placeholder={t('access_token')}
                    value={repo.token}
                    onChange={(e) => {
                      updateGitlabRepo(index, 'token', e.target.value)
                    }}
                  />
                  <Input
                    placeholder={t('branch')}
                    value={repo.branch}
                    onChange={(e) => {
                      updateGitlabRepo(index, 'branch', e.target.value)
                    }}
                  />
                  <Input
                    placeholder={t('tag_placeholder')}
                    value={repo.tag ?? ''}
                    onChange={(e) => {
                      updateGitlabRepo(index, 'tag', e.target.value)
                    }}
                    prefix={<TagIcon size={14} />}
                  />
                  <Button
                    danger
                    size="small"
                    icon={<Trash2 size={14} />}
                    onClick={() => {
                      removeGitlabRepo(index)
                    }}
                  >
                    {t('remove')}
                  </Button>
                </Space>
              </Card>
            ))}
            <Button icon={<Plus size={16} />} onClick={addGitlabRepo} block>
              {t('add_gitlab_repo')}
            </Button>
          </Space>
        </Panel>

        {/* GitHub Repositories */}
        <Panel
          header={
            <Space>
              <GitBranch size={16} />
              <Text strong>{t('github_repos')}</Text>
              <Text type="secondary">({githubRepos.length})</Text>
            </Space>
          }
          key="github"
        >
          <Space vertical style={{ width: '100%' }} size={12}>
            {githubRepos.map((repo, index) => (
              <Card key={`github-${String(index)}`} size="small">
                <Space vertical style={{ width: '100%' }} size={8}>
                  <Input
                    placeholder={t('github_url')}
                    value={repo.url}
                    onChange={(e) => {
                      updateGithubRepo(index, 'url', e.target.value)
                    }}
                  />
                  <Input
                    placeholder={t('project_path_placeholder')}
                    value={repo.project_path}
                    onChange={(e) => {
                      updateGithubRepo(index, 'project_path', e.target.value)
                    }}
                  />
                  <Input.Password
                    placeholder={t('access_token')}
                    value={repo.token}
                    onChange={(e) => {
                      updateGithubRepo(index, 'token', e.target.value)
                    }}
                  />
                  <Input
                    placeholder={t('branch')}
                    value={repo.branch}
                    onChange={(e) => {
                      updateGithubRepo(index, 'branch', e.target.value)
                    }}
                  />
                  <Input
                    placeholder={t('tag_placeholder')}
                    value={repo.tag ?? ''}
                    onChange={(e) => {
                      updateGithubRepo(index, 'tag', e.target.value)
                    }}
                    prefix={<TagIcon size={14} />}
                  />
                  <Button
                    danger
                    size="small"
                    icon={<Trash2 size={14} />}
                    onClick={() => {
                      removeGithubRepo(index)
                    }}
                  >
                    {t('remove')}
                  </Button>
                </Space>
              </Card>
            ))}
            <Button icon={<Plus size={16} />} onClick={addGithubRepo} block>
              {t('add_github_repo')}
            </Button>
          </Space>
        </Panel>

        {/* Confluence Spaces */}
        <Panel
          header={
            <Space>
              <BookOpen size={16} />
              <Text strong>{t('confluence_spaces')}</Text>
              <Text type="secondary">({confluenceSpaces.length})</Text>
            </Space>
          }
          key="confluence"
        >
          <Space vertical style={{ width: '100%' }} size={12}>
            {confluenceSpaces.map((space, index) => (
              <Card key={`confluence-${String(index)}`} size="small">
                <Space vertical style={{ width: '100%' }} size={8}>
                  <Input
                    placeholder={t('confluence_url')}
                    value={space.url}
                    onChange={(e) => {
                      updateConfluenceSpace(index, 'url', e.target.value)
                    }}
                  />
                  <Input
                    placeholder={t('space_key')}
                    value={space.space_key}
                    onChange={(e) => {
                      updateConfluenceSpace(index, 'space_key', e.target.value)
                    }}
                  />
                  <Input
                    placeholder={t('user_email')}
                    value={space.user}
                    onChange={(e) => {
                      updateConfluenceSpace(index, 'user', e.target.value)
                    }}
                  />
                  <Input.Password
                    placeholder={t('api_token')}
                    value={space.api_token}
                    onChange={(e) => {
                      updateConfluenceSpace(index, 'api_token', e.target.value)
                    }}
                  />
                  <Space>
                    <Text>{t('cloud_instance')}</Text>
                    <Switch
                      checked={space.cloud}
                      onChange={(checked) => {
                        updateConfluenceSpace(index, 'cloud', checked)
                      }}
                    />
                  </Space>
                  <Input
                    placeholder={t('tag_placeholder')}
                    value={space.tag ?? ''}
                    onChange={(e) => {
                      updateConfluenceSpace(index, 'tag', e.target.value)
                    }}
                    prefix={<TagIcon size={14} />}
                  />
                  <Button
                    danger
                    size="small"
                    icon={<Trash2 size={14} />}
                    onClick={() => {
                      removeConfluenceSpace(index)
                    }}
                  >
                    {t('remove')}
                  </Button>
                </Space>
              </Card>
            ))}
            <Button icon={<Plus size={16} />} onClick={addConfluenceSpace} block>
              {t('add_confluence_space')}
            </Button>
          </Space>
        </Panel>

        {/* Jira Projects */}
        <Panel
          header={
            <Space>
              <CheckSquare size={16} />
              <Text strong>{t('jira_projects')}</Text>
              <Text type="secondary">({jiraProjects.length})</Text>
            </Space>
          }
          key="jira"
        >
          <Space vertical style={{ width: '100%' }} size={12}>
            {jiraProjects.map((project, index) => (
              <Card key={`jira-${String(index)}`} size="small">
                <Space vertical style={{ width: '100%' }} size={8}>
                  <Input
                    placeholder={t('jira_url')}
                    value={project.url}
                    onChange={(e) => {
                      updateJiraProject(index, 'url', e.target.value)
                    }}
                  />
                  <Input
                    placeholder={t('project_key')}
                    value={project.project_key}
                    onChange={(e) => {
                      updateJiraProject(index, 'project_key', e.target.value)
                    }}
                  />
                  <Input
                    placeholder={t('user_email')}
                    value={project.user}
                    onChange={(e) => {
                      updateJiraProject(index, 'user', e.target.value)
                    }}
                  />
                  <Input.Password
                    placeholder={t('api_token')}
                    value={project.api_token}
                    onChange={(e) => {
                      updateJiraProject(index, 'api_token', e.target.value)
                    }}
                  />
                  <Space>
                    <Text>{t('cloud_instance')}</Text>
                    <Switch
                      checked={project.cloud}
                      onChange={(checked) => {
                        updateJiraProject(index, 'cloud', checked)
                      }}
                    />
                  </Space>
                  <Input
                    placeholder={t('tag_placeholder')}
                    value={project.tag ?? ''}
                    onChange={(e) => {
                      updateJiraProject(index, 'tag', e.target.value)
                    }}
                    prefix={<TagIcon size={14} />}
                  />
                  <Button
                    danger
                    size="small"
                    icon={<Trash2 size={14} />}
                    onClick={() => {
                      removeJiraProject(index)
                    }}
                  >
                    {t('remove')}
                  </Button>
                </Space>
              </Card>
            ))}
            <Button icon={<Plus size={16} />} onClick={addJiraProject} block>
              {t('add_jira_project')}
            </Button>
          </Space>
        </Panel>
      </Collapse>
    </div>
  )
}
